import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null=loading, false=unauth, obj=auth
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data.user);
        } catch {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = useCallback(async (email, password, mfa_code) => {
        const { data } = await api.post("/auth/login", { email, password, mfa_code });
        if (data.mfa_required) return { mfa_required: true };
        setUser(data.user);
        return { user: data.user };
    }, []);

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } catch (err) {
            // Server-side session may already be invalid; still clear client state.
            // eslint-disable-next-line no-console
            console.warn("Logout request failed:", err?.message || err);
        }
        setUser(false);
    }, []);

    const changePassword = useCallback(async (current_password, new_password) => {
        await api.post("/auth/change-password", { current_password, new_password });
        await refresh();
    }, [refresh]);

    const value = useMemo(
        () => ({ user, loading, login, logout, refresh, changePassword, formatApiError }),
        [user, loading, login, logout, refresh, changePassword]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
